import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Mail, 
  Plus, 
  Trash2, 
  Edit, 
  User, 
  Shield, 
  Scissors,
  ExternalLink,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const EmailManagement = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email_prefix: '',
    role: 'barber',
    is_active: true
  });

  const DOMAIN = 'kegentssaloon.com';

  useEffect(() => {
    fetchEmails();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmails = async () => {
    try {
      const res = await api.get('/emails');
      setEmails(res.data);
    } catch (error) {
      // If endpoint doesn't exist yet, use empty array
      setEmails([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        full_email: `${formData.email_prefix}@${DOMAIN}`
      };

      if (editMode && selectedEmail) {
        await api.put(`/emails/${selectedEmail.id}`, payload);
        toast.success('تم تحديث الإيميل بنجاح');
      } else {
        await api.post('/emails', payload);
        toast.success('تم إضافة الإيميل بنجاح');
      }
      
      setOpen(false);
      resetForm();
      fetchEmails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشلت العملية');
    }
  };

  const handleEdit = (email) => {
    setSelectedEmail(email);
    setFormData({
      name: email.name,
      email_prefix: email.email_prefix,
      role: email.role,
      is_active: email.is_active
    });
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (emailId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الإيميل؟')) return;
    try {
      await api.delete(`/emails/${emailId}`);
      toast.success('تم حذف الإيميل بنجاح');
      fetchEmails();
    } catch (error) {
      toast.error('فشل حذف الإيميل');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email_prefix: '',
      role: 'barber',
      is_active: true
    });
    setEditMode(false);
    setSelectedEmail(null);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="w-4 h-4 text-red-500" />;
      case 'barber':
        return <Scissors className="w-4 h-4 text-blue-500" />;
      default:
        return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'مدير';
      case 'barber':
        return 'حلاق';
      default:
        return 'موظف';
    }
  };

  // Admin only page
  if (!isAdmin()) {
    return (
      <div className="text-center py-12">
        <p className="text-xl text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Mail className="w-8 h-8 text-primary" />
            إدارة البريد الإلكتروني
          </h1>
          <p className="text-muted-foreground">إدارة حسابات @{DOMAIN}</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-email-btn" className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 me-2" />
              إضافة إيميل
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle>{editMode ? 'تعديل الإيميل' : 'إضافة إيميل جديد'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">الاسم الكامل</label>
                <Input
                  data-testid="email-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="مثال: أحمد محمد"
                  required
                  className="bg-secondary/50 border-white/10"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">عنوان الإيميل</label>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid="email-prefix-input"
                    value={formData.email_prefix}
                    onChange={(e) => setFormData({ ...formData, email_prefix: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                    placeholder="ahmed"
                    required
                    className="bg-secondary/50 border-white/10"
                    dir="ltr"
                  />
                  <span className="text-muted-foreground whitespace-nowrap">@{DOMAIN}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  سيكون الإيميل: {formData.email_prefix || 'example'}@{DOMAIN}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الدور</label>
                <select
                  data-testid="email-role-select"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full bg-secondary/50 border border-white/10 rounded-lg px-4 py-2"
                >
                  <option value="admin">مدير</option>
                  <option value="barber">حلاق</option>
                  <option value="staff">موظف</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 accent-primary"
                />
                <label htmlFor="is_active" className="text-sm">الحساب نشط</label>
              </div>

              <Button data-testid="submit-email-btn" type="submit" className="w-full bg-primary text-primary-foreground">
                {editMode ? 'تحديث' : 'إضافة'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Google Workspace Info Card */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-500/20 rounded-lg">
            <Mail className="w-6 h-6 text-blue-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-2">إعداد Google Workspace</h3>
            <p className="text-muted-foreground text-sm mb-4">
              لتفعيل الإيميلات الحقيقية @{DOMAIN}، تحتاج للاشتراك في Google Workspace
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-card/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Business Starter</p>
                <p className="text-xl font-bold text-primary">$6<span className="text-sm text-muted-foreground">/شهر/مستخدم</span></p>
              </div>
              <div className="bg-card/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Business Standard</p>
                <p className="text-xl font-bold">$12<span className="text-sm text-muted-foreground">/شهر/مستخدم</span></p>
              </div>
              <div className="bg-card/50 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Business Plus</p>
                <p className="text-xl font-bold">$18<span className="text-sm text-muted-foreground">/شهر/مستخدم</span></p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
              onClick={() => window.open('https://workspace.google.com/pricing.html', '_blank')}
            >
              <ExternalLink className="w-4 h-4 me-2" />
              الاشتراك في Google Workspace
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">إجمالي الحسابات</p>
          <p className="text-3xl font-bold text-primary">{emails.length}</p>
        </div>
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">الحسابات النشطة</p>
          <p className="text-3xl font-bold text-green-500">{emails.filter(e => e.is_active).length}</p>
        </div>
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">النطاق</p>
          <p className="text-xl font-bold text-blue-500">@{DOMAIN}</p>
        </div>
      </div>

      {/* Email List */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <h3 className="text-lg font-semibold">قائمة حسابات البريد الإلكتروني</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">الاسم</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">البريد الإلكتروني</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الدور</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحالة</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {emails.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-muted-foreground">لا توجد حسابات بريد إلكتروني</p>
                    <p className="text-sm text-muted-foreground">اضغط على "إضافة إيميل" لإنشاء حساب جديد</p>
                  </td>
                </tr>
              ) : (
                emails.map((email) => (
                  <tr key={email.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <span className="text-primary font-bold">
                            {email.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <span className="font-medium">{email.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4" dir="ltr">
                      <span className="text-primary">{email.full_email}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getRoleIcon(email.role)}
                        <span>{getRoleLabel(email.role)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {email.is_active ? (
                        <span className="flex items-center gap-1 text-green-500">
                          <CheckCircle className="w-4 h-4" /> نشط
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-500">
                          <AlertCircle className="w-4 h-4" /> معطل
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handleEdit(email)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-500 hover:bg-blue-500/10"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(email.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Setup Instructions */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-500" />
          خطوات تفعيل الإيميلات الحقيقية
        </h3>
        <ol className="space-y-3 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">1</span>
            <span>اشترك في Google Workspace من الرابط أعلاه</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">2</span>
            <span>أضف النطاق kegentssaloon.com وتحقق من ملكيته</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">3</span>
            <span>أضف سجلات MX في Squarespace DNS</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="bg-primary/20 text-primary w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0">4</span>
            <span>أنشئ حسابات المستخدمين من لوحة تحكم Google Workspace</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

export default EmailManagement;
